/**
 * Dynamic Add to Cart Price Updater
 * Updates the add to cart button to show total price based on quantity.
 *
 * Rewritten for performance:
 *  - 2 event listeners (1 click delegation, 1 change delegation) instead of 10+
 *  - Variant JSON parsed once and cached
 *  - Single debounced update path (no stacked setTimeouts)
 *  - 1 MutationObserver (button watcher) instead of 3
 */
class DynamicAddToCartPrice {
  constructor() {
    this.initialized = false;
    this._variantCache = null;       // parsed once, reused forever
    this._volumeCache = new Map();   // variantId → volume pricing array
    this._updateTimer = 0;
    this._isUpdatingButton = false;
    this._buttonObserver = null;
    this._observedButtons = new WeakSet();

    this._init();
  }

  /* ── Bootstrap ─────────────────────────────────────────────── */

  _init() {
    if (this._tryInit()) return;
    // Single fallback - if elements aren't ready yet, wait once
    setTimeout(() => this._tryInit(), 500);
  }

  _tryInit() {
    if (this.initialized) return true;

    const btn = document.querySelector('[name="add"]');
    const vid = document.querySelector('input[name="id"]');
    if (!btn || !vid) return false;

    this._buildVariantCache();
    this._bindEvents();
    this.initialized = true;
    this._scheduleUpdate();
    return true;
  }

  /* ── Variant cache (parse JSON once) ───────────────────────── */

  _buildVariantCache() {
    if (this._variantCache) return;

    const map = new Map();
    document.querySelectorAll('script[type="application/json"]').forEach(script => {
      try {
        const parsed = JSON.parse(script.textContent);
        let variants;
        if (Array.isArray(parsed)) {
          variants = parsed;
        } else if (parsed && Array.isArray(parsed.variants)) {
          variants = parsed.variants;
          // Also cache volume pricing if present
          parsed.variants.forEach(v => {
            if (v && v.id && v.quantity_price_breaks) {
              this._volumeCache.set(String(v.id), v.quantity_price_breaks.map(b => ({
                minimum_quantity: b.quantity,
                price: b.price
              })));
            }
          });
        } else if (parsed && parsed.variant && parsed.variant.id) {
          variants = [parsed.variant];
        } else {
          return;
        }
        if (!Array.isArray(variants)) return;
        variants.forEach(v => { if (v && v.id) map.set(String(v.id), v); });
      } catch (_) { /* not JSON we care about */ }
    });

    if (window.variantData && Array.isArray(window.variantData)) {
      window.variantData.forEach(v => { if (v && v.id && !map.has(String(v.id))) map.set(String(v.id), v); });
    }

    this._variantCache = map;
  }

  /* ── Events (2 listeners total) ─────────────────────────────── */

  _bindEvents() {
    // Single delegated click - covers variant radios, labels, pack cards
    document.addEventListener('click', e => {
      const t = e.target;
      if (
        (t.type === 'radio' && t.name && t.name.startsWith('options[')) ||
        (t.tagName === 'LABEL' && t.closest('.product-form__input')) ||
        t.closest('.product-form__input') ||
        t.closest('.variant-selector') ||
        t.closest('.pack-card')
      ) {
        this._scheduleUpdate();
      }
    });

    // Single delegated change - covers quantity, variant id, radio options
    document.addEventListener('change', e => {
      const n = e.target.name;
      if (n === 'quantity' || n === 'id' || (n && n.startsWith('options['))) {
        this._scheduleUpdate();
      }
    });

    // Pack selector attribute observer (only if element exists)
    const packSelector = document.querySelector('.pack-selector');
    if (packSelector) {
      const obs = new MutationObserver(() => this._scheduleUpdate());
      packSelector.querySelectorAll('.pack-card').forEach(card => {
        obs.observe(card, { attributes: true, attributeFilter: ['aria-checked', 'class'] });
      });
    }

    // Button text watcher - one observer for all buttons
    this._setupButtonWatcher();
  }

  /* ── Button watcher (1 MutationObserver) ────────────────────── */

  _setupButtonWatcher() {
    this._buttonObserver = new MutationObserver(mutations => {
      if (this._isUpdatingButton) return;
      this._scheduleUpdate();
    });

    document.querySelectorAll('[name="add"]').forEach(btn => {
      if (this._observedButtons.has(btn)) return;
      this._observedButtons.add(btn);
      this._buttonObserver.observe(btn, { childList: true, subtree: true, characterData: true });
    });
  }

  /* ── Debounced update (single path) ─────────────────────────── */

  _scheduleUpdate() {
    clearTimeout(this._updateTimer);
    this._updateTimer = setTimeout(() => this._doUpdate(), 150);
  }

  _doUpdate() {
    if (!this.initialized) return;

    // The bundle selector (sleep-mask-bundle.liquid - Migraine Cap / Sleep Mask) owns
    // the button price on its pages: it computes flat-fallback bundle totals this class
    // doesn't know about, and it reads the qty input reliably. Don't clobber it back to
    // the qty×unit price (which, with the qty input outside the form, always read 1).
    if (document.querySelector('[data-smb]')) return;

    document.querySelectorAll('[name="add"]').forEach(button => {
      const form = button.closest('form');
      if (!form) return;

      // Quantity
      let qty = 1;
      const qtyInput = form.querySelector('input[name="quantity"]') || form.querySelector('input[type="number"]');
      if (qtyInput) {
        qty = parseInt(qtyInput.value) || 1;
      } else {
        const sel = document.querySelector('.pack-card[aria-checked="true"]');
        if (sel) qty = parseInt(sel.getAttribute('data-qty')) || 1;
      }

      // Variant
      const vidInput = form.querySelector('input[name="id"]');
      if (!vidInput) return;
      const variantId = String(vidInput.value);
      const variant = this._variantCache && this._variantCache.get(variantId);
      if (!variant || !variant.price) return;

      // Price calculation
      const priceData = this._calcPrice(variant, qty);
      if (!priceData) return;

      this._updateButton(button, priceData, qty);
    });
  }

  /* ── Price calculation ──────────────────────────────────────── */

  _calcPrice(variant, quantity) {
    let unitPrice = variant.price;
    let discountText = '';

    // Check cached volume pricing first
    const vol = this._volumeCache.get(String(variant.id)) || this._parseVolumePricing(variant.id);

    if (vol && vol.length > 0) {
      const applicable = vol.filter(b => quantity >= b.minimum_quantity).sort((a, b) => b.minimum_quantity - a.minimum_quantity)[0];
      if (applicable) {
        unitPrice = applicable.price;
        if (applicable.price < variant.price) {
          discountText = ` (${Math.round(((variant.price - applicable.price) / variant.price) * 100)}% off)`;
        }
      }
    } else {
      // Fall back to pack badge discount
      const packCard = document.querySelector('.pack-card.selected, .pack-card.is-selected, .pack-card[aria-checked="true"]');
      if (packCard) {
        const badge = packCard.querySelector('.pack-badge') || packCard.querySelector('[class*="badge"]') || packCard.querySelector('span');
        if (badge) {
          const m = badge.textContent.match(/(\d+)%/);
          if (m) {
            const pct = parseInt(m[1]);
            unitPrice = Math.round(variant.price * (100 - pct) / 100);
            discountText = ` (${pct}% off)`;
          }
        }
      }
    }

    return { price: this._fmt(unitPrice * quantity), discountText, unitPrice, totalPrice: unitPrice * quantity };
  }

  _parseVolumePricing(variantId) {
    const el = document.getElementById(`Volume-${variantId}`);
    if (!el) return null;
    const pricing = [];
    el.querySelectorAll('li').forEach(li => {
      const qtySpan = li.querySelector('span:first-child');
      const priceSpan = li.querySelector('span:not(:first-child):last-child');
      if (qtySpan && priceSpan) {
        const q = parseInt(qtySpan.textContent.replace(/[^\d]/g, ''));
        const pm = (priceSpan.dataset.text || priceSpan.textContent).match(/[\d,]+\.?\d*/);
        if (pm) pricing.push({ minimum_quantity: q, price: parseFloat(pm[0].replace(',', '')) * 100 });
      }
    });
    if (pricing.length) {
      pricing.sort((a, b) => a.minimum_quantity - b.minimum_quantity);
      this._volumeCache.set(String(variantId), pricing);
    }
    return pricing.length ? pricing : null;
  }

  /* ── Formatting ─────────────────────────────────────────────── */

  _fmt(cents) {
    if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(cents);
    const cur = window.Shopify?.currency?.active || 'AUD';
    const syms = { USD: '$', AUD: 'AUD $', EUR: '€', GBP: '£', CAD: 'CAD $' };
    return (syms[cur] || cur + ' ') + (cents / 100).toFixed(2);
  }

  /* ── DOM update ─────────────────────────────────────────────── */

  _updateButton(button, priceData, quantity) {
    const span = button.querySelector('span');
    if (!span || button.hasAttribute('disabled')) return;
    // A pre-order variant's button reads its plan name ("Pre-order: ships by
    // 21 Nov"), set by assets/product-form.js. Leave it as it is.
    if (button.hasAttribute('data-preorder-label')) return;

    this._isUpdatingButton = true;
    const label = span.textContent.includes('ADD TO CART') ? 'ADD TO CART' : 'Add to cart';
    span.innerHTML = `${label} <span class="price-separator">|</span> ${priceData.price}${priceData.discountText}`;
    button.setAttribute('data-total-price', priceData.price);
    button.setAttribute('data-quantity', quantity);
    if (priceData.discountText) button.setAttribute('data-discount', priceData.discountText);

    // Reset flag synchronously on next microtask (faster than setTimeout)
    queueMicrotask(() => { this._isUpdatingButton = false; });
  }
}

// Init
const dynamicPriceUpdater = new DynamicAddToCartPrice();
window.dynamicPriceUpdater = dynamicPriceUpdater;

// Public API (backwards compat)
window.updatePriceOnVariantChange = () => dynamicPriceUpdater._scheduleUpdate();
window.forcePriceUpdate = () => dynamicPriceUpdater._scheduleUpdate();
window.forceInit = () => { dynamicPriceUpdater.initialized = false; dynamicPriceUpdater._tryInit(); };
